/**
 * @file chart.component.tsx
 * @description Основной компонент диаграмм для отображения интерактивных графиков и аналитических данных в Task Manager приложении. Обеспечивает полный функционал работы с диаграммами включая загрузку данных через ChartService, систему фильтрации с возможностью сохранения настроек, поиск по содержимому, управление видимостью и размерами колонок, модальные окна для редактирования объектов и обработку ошибок. Поддерживает различные типы диаграмм через динамический выбор компонентов. Полностью типизирован с интерфейсами IChartProps и IChartColumn для строгой типизации props и конфигурации колонок.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['@src/components/ui/visualizations/charts/gantt', '@features/page-modal', '@features/catalog/components', '@components/ui/indicators/error-list', 'react', '@hooks/use-modals', '@api/actions/chart.service', '@components/ui', '@features/filter', '@features/catalog/context/search-text-context', '@src/types/dto/base.dto.interface', '@features/filter/types', '@features/object-form', '@utils/form-object-helper', 'axios', '@utils/error-helper']
 * @related-files ['src/features/chart/chart.provider.tsx', 'src/components/ui/charts/gantt', 'src/api/actions/chart.service.ts', 'src/features/catalog/components/header.component.tsx']
 */

import { Gantt } from '@src/components/ui/visualizations/charts/gantt';
import { PageModal } from '@components/ui/layout/page-modal';
import { Header } from '@features/catalog/components';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useModalQueue } from '@hooks/use-modals';
import { useSavedFilters } from '@features/filter';
import { ChartService } from '@api/actions/chart.service';
import { Loader } from '@components/ui';
import {
    getActiveFilters,
    getInitialValuesFromFilterControls,
    prepareFilterControlsToFetch,
    prepareFilterFormToFetch
} from '@features/filter';
import { ToolbarTags as FilterTagLine } from '@features/filter';
import { FilterModal } from '@features/filter';
import { useSearchText } from '@features/catalog/context/search-text-context';
import { IBaseObjectDTO } from '@src/types/dto/base.dto.interface';
import { filterComponent } from '@features/filter/types';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@utils/form-object-helper';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { setErrors } from '@utils/error-helper';
import { TBaseObjectDto } from '@src/types';
import { GridColumn } from 'dhx-gantt';
import { IGanttData } from '@src/types/gantt';
import { TPageConfig } from '../../types/pages';

/**
 * Интерфейс props для компонента Chart
 */
interface IChartProps {
    /** HTML контейнер для рендеринга модального окна диаграммы */
    container?: HTMLDivElement;
    /** Заголовок диаграммы, отображаемый в Header компоненте */
    title?: string;
    /** Массив конфигураций колонок для отображения данных в диаграмме */
    columns: GridColumn[];
    /** Тип диаграммы, определяет какой компонент будет использован для рендеринга */
    type: string;
    /** Имя диаграммы для API запросов через ChartService.get() */
    name: string;
    /** Callback функция, вызываемая при закрытии диаграммы */
    onClose?: () => void;
    /** Функция для получения конфигурации фильтров */
    getFilterControls?: (savedFilters?: TBaseObjectDto | null) => filterComponent[];
}

export const Chart = ({
    container,
    title = 'Диаграмма',
    columns: initialColumns,
    type,
    name,
    onClose,
    getFilterControls
}: IChartProps) => {
    const [isShow, setIsShow] = useState<boolean>(true);
    const [data, setData] = useState<IGanttData | null>(null);
    const [extra, setExtra] = useState<TBaseObjectDto | undefined>(undefined);
    const [currentObject, setCurrentObject] = useState<TBaseObjectDto | undefined>(undefined);
    const { searchText } = useSearchText();
    const [filterControls, setFilterControls] = useState<filterComponent[]>(
        getFilterControls ? getFilterControls() : []
    );
    const activeFilters = useMemo(() => getActiveFilters(filterControls), [filterControls]);
    const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
    const errorListRef = useRef<ErrorListHandle | null>(null);
    const objectEntityRef = useRef<TPageConfig | null>(null);
    const filterControlsRef = useRef<filterComponent[]>(filterControls);
    const { getFilters, saveFilters } = useSavedFilters(type);
    const columns = useMemo(() => {
        let columnList = initialColumns.filter(
            (column: GridColumn) =>
                !filterControls.find(
                    (filter: filterComponent) =>
                        filter.filterType === 'columnVisibility' &&
                        !filter.value &&
                        filter.targetColumn &&
                        filter.targetColumn === column.name
                )
        );

        columnList = columnList.map((column: GridColumn) => {
            if (typeof column.onrender === 'string' && column.onrender === 'link') {
                column.onrender = (item: { [key: string]: any; $item?: IBaseObjectDTO }) =>
                    item.$item?.$type ? (
                        <div
                            className="wrapper hover:text-link-hover cursor-pointer"
                            title={item[column.name || '']}
                            onClick={() => {
                                setCurrentObject(item.$item || undefined);
                                objectEntityRef.current = getObjectEntity(item.$item!.$type);
                            }}
                        >
                            {item[column.name || '']}
                        </div>
                    ) : (
                        item[column.name || '']
                    );
            }

            const columnsSettings = filterControls.find(
                (filter: filterComponent) => filter.filterType === 'columnsSettings' && filter.value?.length
            );

            if (columnsSettings) {
                const columnSettings = columnsSettings.value.find(
                    (setting: { name: string }) => setting.name === column.name
                );

                if (columnSettings) {
                    return { ...column, ...columnSettings };
                }
            }

            return column;
        });

        return columnList;
    }, [initialColumns, filterControls]);

    const closePageHandler = useModalQueue(() => {
        setIsShow(false);
        onClose && onClose();
    }, type);
    const [modalContainer, setModalContainer] = useState<HTMLDivElement | undefined>(container);

    useEffect(() => {
        async function tryFecthSavedFilters() {
            const savedFilters: TBaseObjectDto = await getFilters();
            const newFilterControls: filterComponent[] = getFilterControls ? getFilterControls(savedFilters) : [];
            setFilterControls(newFilterControls);
            fetchData(savedFilters, newFilterControls);
        }

        tryFecthSavedFilters();
    }, [type]);

    useEffect(() => {
        setModalContainer(container || (document.getElementById('MainLayout') as HTMLDivElement));
    }, []);

    useEffect(() => {
        filterControlsRef.current = filterControls;
    }, [filterControls]);

    useEffect(() => {
        if (data) {
            const timeOutId = setTimeout(() => fetchData(), 500);

            return () => clearTimeout(timeOutId);
        }
    }, [searchText]);

    const fetchData = async (
        filters: TBaseObjectDto | null = null,
        _filterControls: filterComponent[] = filterControlsRef.current
    ) => {
        let filterItems = filters
            ? prepareFilterFormToFetch(filters, filterControls)
            : prepareFilterControlsToFetch(_filterControls);

        const response: AxiosResponse | AxiosError = await ChartService.get(name, {
            searchText,
            filterItems
        });

        if (axios.isAxiosError(response)) {
            errorListRef.current && setErrors(response, errorListRef.current);
            return;
        }

        response.data.chart && setData(response.data.chart);
        response.data.extra && setExtra(response.data.extra);
    };

    const onColumnResize = (column: GridColumn, newWidth: number) => {
        const newFilterControls = filterControls.map((control: filterComponent) => {
            if (control.filterType === 'columnsSettings') {
                let newValue = control.value || [];
                newValue = newValue.filter((val: { name: string }) => val.name !== column.name);
                newValue = [...newValue, { name: column.name, width: newWidth }];

                return { ...control, value: newValue };
            }

            return control;
        });

        setFilterControls(newFilterControls);
        saveFilters(newFilterControls);
    };

    return (
        <PageModal container={modalContainer} closePageHandler={closePageHandler} isOpen={isShow}>
            <div className="chart__feature gap-default h-full w-full relative">
                <div className="chart__feature-wrapper absolute w-full h-full flex flex-col pr-[3.1756vmin]">
                    <Header
                        title={title}
                        closePageHandler={closePageHandler}
                        filterControls={filterControls}
                        onFilterButtonClick={() => setIsShowFilterModal(true)}
                        isShowFilterModal={isShowFilterModal}
                        isCanShowModal={true}
                    />

                    {activeFilters.length > 0 && (
                        <FilterTagLine
                            filters={activeFilters}
                            onFilterClick={() => setIsShowFilterModal(true)}
                            onClearFilters={(filterName: string | undefined) => {
                                let newFilterControls: filterComponent[] = [];

                                if (filterName) {
                                    newFilterControls = filterControls.map((filterControl: filterComponent) => {
                                        if (filterControl.name !== filterName) {
                                            return filterControl;
                                        }

                                        return { ...filterControl, value: null };
                                    });
                                } else {
                                    newFilterControls = getFilterControls ? getFilterControls() : [];
                                }

                                setFilterControls(newFilterControls);
                                fetchData(getInitialValuesFromFilterControls(newFilterControls));
                                saveFilters(newFilterControls);
                            }}
                        />
                    )}

                    <div className="chart__feature-content flex flex-1 overflow-x-hidden gap-default w-full h-full mb-default">
                        {data ? (
                            createElement(getChartComponentByType(type), {
                                data,
                                columns,
                                extra,
                                onColumnResize
                            })
                        ) : (
                            <Loader />
                        )}
                    </div>
                </div>

                <ErrorList ref={errorListRef} />

                {isShowFilterModal && (
                    <FilterModal
                        container={modalContainer}
                        isShow={isShowFilterModal}
                        setIsShow={setIsShowFilterModal}
                        filterControls={filterControls}
                        applyNewFilterControls={(filters: filterComponent[]) => {
                            setFilterControls(filters);
                            saveFilters(filters);
                        }}
                        fetchDataHadler={(filters: IBaseObjectDTO[]) => fetchData(filters[0] || null)}
                        itemType={type}
                    />
                )}

                {Boolean(currentObject) && objectEntityRef.current && (
                    <ObjectForm
                        type={currentObject!.$type}
                        initialItem={currentObject}
                        afterSave={() => fetchData()}
                        onClose={() => {
                            setCurrentObject(undefined);
                            objectEntityRef.current = null;
                        }}
                        {...objectEntityRef.current}
                    />
                )}
            </div>
        </PageModal>
    );
};

const getChartComponentByType = (type: string) => {
    switch (type) {
        case 'gantt':
            return Gantt;
        default:
            return Gantt;
    }
};
